/**
 * Coalesces a burst of server pushes into a single refresh, and spreads that
 * refresh across a jitter window so every connected client doesn't hit the API
 * on the same tick.
 *
 * A dispatch board push says "something changed" — it carries no data — so the
 * client answers it with a refetch. One dispatcher assigning a dozen moves
 * emits a dozen broadcasts in a couple of seconds; without the trailing
 * debounce that's a dozen refetches on every open dashboard. The jitter matters
 * for the other axis: all N clients receive the same broadcast simultaneously,
 * so a bare debounce would just move the stampede three seconds to the right.
 */
export interface PushRefreshSchedulerOptions {
  /** Called once per coalesced burst. */
  onFire: () => void;
  /** Trailing debounce window — how long to wait for the burst to settle. */
  debounceMs: number;
  /** Upper bound on the random delay added after the debounce window. */
  maxJitterMs: number;
  /** Injectable for tests; defaults to Math.random. */
  random?: () => number;
}

export interface PushRefreshScheduler {
  /** Note that a push arrived. Restarts the debounce window. */
  schedule: () => void;
  /** Drop any pending refresh (unmount, or the page going quiet). */
  cancel: () => void;
}

export function createPushRefreshScheduler({
  onFire,
  debounceMs,
  maxJitterMs,
  random = Math.random,
}: PushRefreshSchedulerOptions): PushRefreshScheduler {
  let timer: ReturnType<typeof setTimeout> | null = null;

  const cancel = () => {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
  };

  const schedule = () => {
    cancel();
    const delay = debounceMs + Math.floor(random() * maxJitterMs);
    timer = setTimeout(() => {
      timer = null;
      onFire();
    }, delay);
  };

  return { schedule, cancel };
}
